/** Materialise a canned archetype (or a whole group) into one tenant.
 *
 *  Two-step by design: the dialog runs a DRY RUN first and shows what would
 *  happen per archetype, and only then offers the write. Materialising is a
 *  cross-tenant act that creates notifications; "I clicked it and something
 *  appeared" is not good enough for that.
 *
 *  The notifications it creates are SWITCHED OFF. The dialog says so, because
 *  the natural assumption after "materialise" is that messages start going out.
 */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import * as cannedApi from '@/api/cannedNotifications';
import type {
  CannedGroup,
  CannedNotificationListItem,
  MaterialiseResult,
} from '@/api/cannedNotifications';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

const STATUS_COLOUR: Record<string, 'success' | 'info' | 'default' | 'warning'> = {
  created: 'success',
  refreshed: 'info',
  unchanged: 'default',
  skipped: 'warning',
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Materialise exactly one archetype… */
  canned?: CannedNotificationListItem | null;
  /** …or offer a whole group with per-archetype checkboxes. */
  group?: CannedGroup | null;
  /** Called after a successful (non-dry) run. */
  onDone?: () => void;
}

export function MaterialiseDialog({ open, onClose, canned, group, onDone }: Props) {
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [selected, setSelected] = useState<string[] | null>(null);
  const [preview, setPreview] = useState<MaterialiseResult | null>(null);
  const [applied, setApplied] = useState<MaterialiseResult | null>(null);

  const archetypes = useMemo(
    () => (group ? group.notifications.filter((n) => n.enabled) : canned ? [canned] : []),
    [group, canned],
  );
  const codenames = selected ?? archetypes.map((a) => a.codename);

  const run = useMutation({
    mutationFn: async (dryRun: boolean) => {
      if (!tenant) throw new Error('pick an operator');
      if (group) {
        return cannedApi.materialiseGroup(group.id, {
          tour_operator: tenant.id,
          codenames,
          dry_run: dryRun,
        });
      }
      return cannedApi.materialise(canned!.id, { tour_operator: tenant.id, dry_run: dryRun });
    },
    onSuccess: (result, dryRun) => {
      if (dryRun) setPreview(result);
      else {
        setApplied(result);
        onDone?.();
      }
    },
  });

  const title = group ? `Materialise “${group.name}”` : `Materialise “${canned?.name ?? ''}”`;
  const results = applied ?? preview;

  const reset = () => {
    setPreview(null);
    setApplied(null);
  };

  const toggle = (codename: string) => {
    reset();
    setSelected((prev) => {
      const base = prev ?? archetypes.map((a) => a.codename);
      return base.includes(codename)
        ? base.filter((c) => c !== codename)
        : [...base, codename];
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Materialised notifications are created <strong>switched off</strong>. Nothing is sent
            until someone reads them and enables them.
          </Alert>

          <TenantPicker
            value={tenant}
            onChange={(v) => {
              setTenant(v);
              reset();
            }}
            label="Operator"
          />

          {group && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Messages
              </Typography>
              {archetypes.map((a) => (
                <FormControlLabel
                  key={a.codename}
                  control={
                    <Checkbox
                      size="small"
                      checked={codenames.includes(a.codename)}
                      onChange={() => toggle(a.codename)}
                    />
                  }
                  label={
                    <span>
                      {a.name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        {a.event} · {a.channels.join(', ')}
                      </Typography>
                    </span>
                  }
                  sx={{ display: 'flex' }}
                />
              ))}
            </Box>
          )}

          {run.isError && (
            <Alert severity="error">
              {(run.error as { body?: { detail?: string } })?.body?.detail ??
                'The materialisation could not run.'}
            </Alert>
          )}

          {results && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {applied ? 'Done' : 'Preview — nothing written yet'}
              </Typography>
              <Stack spacing={0.5}>
                {results.results.map((r) => (
                  <Stack key={r.codename} direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={r.status} color={STATUS_COLOUR[r.status] ?? 'default'} />
                    <Typography variant="body2">{r.codename}</Typography>
                    {r.reason && (
                      <Typography variant="caption" color="text.secondary">
                        {r.reason}
                      </Typography>
                    )}
                    {r.changed.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {r.changed.join(', ')}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{applied ? 'Close' : 'Cancel'}</Button>
        {!applied && (
          <Button
            onClick={() => run.mutate(true)}
            disabled={!tenant || codenames.length === 0 || run.isPending}
          >
            Preview
          </Button>
        )}
        {!applied && (
          <Button
            variant="contained"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => run.mutate(false)}
            disabled={!preview || run.isPending}
          >
            Materialise
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
